import type { PlaeePositionResponseDto } from '@/lib/models';

/**
 * One portfolio card: an event and the user's open positions under it.
 * The Figma portfolio card unit is the EVENT (category chip, title, countdown),
 * with the per-outcome positions expandable inside it.
 */
export interface EventPositionGroup {
  /** Stable render key — the event id (event+market for recurring windows), falling back per position when unlinked. */
  key: string;
  /** Local event id (`/events/:eventId` route + lazy event-detail fetch). Absent when the catalog link is missing. */
  eventId?: string;
  title: string;
  category?: string;
  imageUrl?: string;
  /** ISO 8601 trading close — drives the date line and the countdown pill. */
  closesAt?: string;
  /** True for a recurring-crypto (snake) window — one card per window, snake-page navigation. */
  recurring: boolean;
  /** ISO 8601 window open (recurring only) — the date line shows start–end. */
  opensAt?: string;
  /** Snake route params (recurring only). */
  instrument?: string;
  subCategoryCode?: string;
  positions: PlaeePositionResponseDto[];
  /** Σ cost basis + unrealized P&L snapshot, dollars. */
  totalValue: number;
  /** Σ held contracts. */
  totalContracts: number;
  /** Σ unrealized P&L snapshot, dollars (the "Value +$X" badge). */
  totalPnl: number;
}

/**
 * Grouping key for a position's card. Ordinary positions group per EVENT; a
 * recurring-crypto (snake) position groups per WINDOW (event+market) — every
 * 5-min/20-min/... window is its own card. Catalog-unlinked positions each get
 * their own key so they stay visible rather than lumping into a fake card.
 */
export function positionGroupKey(p: PlaeePositionResponseDto): string {
  if (!p.eventId) return `position:${p.positionId}`;
  return p.recurring ? `${p.eventId}:${p.marketId ?? p.positionId}` : p.eventId;
}

/**
 * Group open positions for the portfolio Positions tab — per event, or per
 * window for recurring-crypto markets ({@link positionGroupKey}) — sorted
 * soonest-resolution-first (`closesAt` asc, unknown close last).
 */
export function groupPositionsByEvent(positions: PlaeePositionResponseDto[]): EventPositionGroup[] {
  const groups = new Map<string, EventPositionGroup>();
  for (const p of positions) {
    const key = positionGroupKey(p);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        eventId: p.eventId,
        // A recurring window titles by its market (the per-window question with
        // the strike); ordinary cards title by the event.
        title: (p.recurring ? p.marketName : undefined) ?? p.eventTitle ?? p.marketName ?? p.outcomeName ?? 'Market',
        category: p.category,
        imageUrl: p.eventImageUrl ?? p.categoryIconUrl,
        closesAt: p.closesAt,
        recurring: p.recurring,
        opensAt: p.opensAt,
        instrument: p.instrument,
        subCategoryCode: p.subCategoryCode,
        positions: [],
        totalValue: 0,
        totalContracts: 0,
        totalPnl: 0,
      };
      groups.set(key, g);
    }
    g.positions.push(p);
    g.totalValue += (p.cost ?? 0) + (p.unrealizedPnl ?? 0);
    g.totalContracts += p.quantity;
    g.totalPnl += p.unrealizedPnl ?? 0;
    // The earliest close among the event's markets drives the countdown.
    if (p.closesAt && (!g.closesAt || p.closesAt < g.closesAt)) g.closesAt = p.closesAt;
  }
  return [...groups.values()].sort(byClosesAtAsc);
}

/** Soonest resolution first; entries without a close time sort last. */
export function byClosesAtAsc(a: { closesAt?: string }, b: { closesAt?: string }): number {
  if (!a.closesAt) return b.closesAt ? 1 : 0;
  if (!b.closesAt) return -1;
  return a.closesAt < b.closesAt ? -1 : a.closesAt > b.closesAt ? 1 : 0;
}

/**
 * `"Dec 31 @ 12:00 PM – 12:05 PM"` — a recurring window's start–end date line.
 * Falls back to {@link formatEventDate} of the close when the open is missing.
 */
export function formatWindowRange(opensAt: string | undefined, closesAt: string | undefined): string {
  const start = formatEventDate(opensAt);
  if (!start) return formatEventDate(closesAt);
  const end = closesAt ? new Date(closesAt) : null;
  if (!end || Number.isNaN(end.getTime())) return start;
  return `${start} – ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * `"Dec 31 @ 3:00 PM"` — the Figma card date line. Empty string when
 * absent/invalid. Locale is pinned to en-US like the money formatters, so
 * rendering (and specs) don't drift with the machine locale.
 */
export function formatEventDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} @ ${time}`;
}

/**
 * One History card: an event and the aggregate of the user's finished
 * (sold/settled) positions under it. All money figures are DOLLARS — the card
 * shows what went in, what came back, and the net (not per-contract prices).
 */
export interface FinishedEventGroup {
  /** Stable render key — the event id (event+market for recurring windows), falling back per position when unlinked. */
  key: string;
  eventId?: string;
  title: string;
  category?: string;
  imageUrl?: string;
  closesAt?: string;
  /** True for a recurring-crypto (snake) window — one card per window, snake-page navigation. */
  recurring: boolean;
  /** ISO 8601 window open (recurring only) — the date line shows start–end. */
  opensAt?: string;
  /** Snake route params (recurring only). */
  instrument?: string;
  subCategoryCode?: string;
  /** Distinct answer labels across the event's finished positions ("Your Pick"). */
  picks: string[];
  /** Σ cost basis — total dollars the user put into the event ("Buy in"). */
  totalCost: number;
  /** Σ realized P&L, dollars (the card's chip). */
  totalPnl: number;
  /** Proceeds = totalCost + totalPnl — dollars back out ("Exit"). */
  proceeds: number;
  /** Σ contracts bought ("Quantity"). */
  totalQuantity: number;
  /** Realized return, `totalPnl / totalCost` as a percentage; null when cost is unknown. */
  pnlPct: number | null;
  /** Earliest position open — the "Bought" timestamp. */
  openedAt?: string;
  /** Latest close/settle — the "Sold/Settled" timestamp and the History sort key. */
  finishedAt?: string;
  /** True when the event resolved (any position SETTLED) vs. sold out early. */
  settled: boolean;
}

/**
 * Group finished positions by their event for the portfolio History tab,
 * sorted most-recently-finished first. Positions without a catalog link each
 * get their own group, mirroring {@link groupPositionsByEvent}.
 */
export function groupFinishedPositionsByEvent(positions: PlaeePositionResponseDto[]): FinishedEventGroup[] {
  const groups = new Map<string, FinishedEventGroup>();
  for (const p of positions) {
    const key = positionGroupKey(p);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        eventId: p.eventId,
        title: (p.recurring ? p.marketName : undefined) ?? p.eventTitle ?? p.marketName ?? p.outcomeName ?? 'Market',
        category: p.category,
        imageUrl: p.eventImageUrl ?? p.categoryIconUrl,
        closesAt: p.closesAt,
        recurring: p.recurring,
        opensAt: p.opensAt,
        instrument: p.instrument,
        subCategoryCode: p.subCategoryCode,
        picks: [],
        totalCost: 0,
        totalPnl: 0,
        proceeds: 0,
        totalQuantity: 0,
        pnlPct: null,
        openedAt: undefined,
        finishedAt: undefined,
        settled: false,
      };
      groups.set(key, g);
    }
    const pick = p.outcomeName ?? (p.side === 'LONG' ? 'Yes' : 'No');
    if (!g.picks.includes(pick)) g.picks.push(pick);
    g.totalCost += p.cost ?? 0;
    g.totalPnl += p.realizedPnl ?? 0;
    g.totalQuantity += p.quantity || Math.abs(p.netPosition ?? 0);
    g.settled ||= p.status === 'SETTLED';
    if (p.openedAt && (!g.openedAt || p.openedAt < g.openedAt)) g.openedAt = p.openedAt;
    if (p.closedAt && (!g.finishedAt || p.closedAt > g.finishedAt)) g.finishedAt = p.closedAt;
  }
  for (const g of groups.values()) {
    g.proceeds = g.totalCost + g.totalPnl;
    g.pnlPct = g.totalCost > 0 ? Math.round((g.totalPnl / g.totalCost) * 1000) / 10 : null;
  }
  return [...groups.values()].sort((a, b) => {
    if (!a.finishedAt) return b.finishedAt ? 1 : 0;
    if (!b.finishedAt) return -1;
    return a.finishedAt > b.finishedAt ? -1 : a.finishedAt < b.finishedAt ? 1 : 0;
  });
}
